// src/modules/profile/components/StravaConnectCard/index.js
import React, { useState, useEffect } from 'react';
import { Card, Button, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useService } from '../../../../shared/hooks/useService';
import './styles.css';

const StravaIcon = ({ width = 20, height = 20 }) => (
    <svg className="strava-icon" viewBox="0 0 24 24" width={width} height={height} fill="currentColor">
        <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
    </svg>
);

const StravaConnectCard = ({ isOwnProfile, stravaInfo }) => {
    const service = useService();
    const [stravaStatus, setStravaStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDisconnecting, setIsDisconnecting] = useState(false);

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const status = await service.getStravaStatus();
                setStravaStatus(status);
            } catch (error) {
                console.error('Error fetching Strava status:', error);
            } finally {
                setIsLoading(false);
            }
        };

        if (isOwnProfile) {
            fetchStatus();
        } else {
            setIsLoading(false);
        }
    }, [service, isOwnProfile]);

    const handleConnect = async () => {
        try {
            await service.connectStrava();
        } catch (error) {
            console.error('Error connecting Strava:', error);
        }
    };

    const handleDisconnect = async () => {
        setIsDisconnecting(true);
        try {
            await service.disconnectStrava();
            setStravaStatus({ connected: false });
        } catch (error) {
            console.error('Error disconnecting Strava:', error);
        } finally {
            setIsDisconnecting(false);
        }
    };

    // Other user's profile: show connected badge if they have Strava linked
    if (!isOwnProfile) {
        if (!stravaInfo?.connected) return null;

        const profileUrl = `https://www.strava.com/athletes/${stravaInfo.athleteId}`;
        return (
            <Card className="strava-connect-card mb-4">
                <Card.Header>
                    <h5 className="mb-0">
                        <StravaIcon className="me-2" />
                        <span className="ms-2">Strava</span>
                    </h5>
                </Card.Header>
                <Card.Body>
                    <a
                        href={profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="strava-profile-link d-flex align-items-center text-decoration-none"
                    >
                        <span className="text-success me-2">
                            <i className="bi bi-check-circle-fill"></i>
                        </span>
                        <span className="me-1">Connected as</span>
                        <strong className="me-2">{stravaInfo.athleteName}</strong>
                        <i className="bi bi-box-arrow-up-right text-muted small"></i>
                    </a>
                </Card.Body>
            </Card>
        );
    }

    if (isLoading) return null;

    return (
        <Card className="strava-connect-card mb-4">
            <Card.Header>
                <h5 className="mb-0">
                    <StravaIcon className="me-2" />
                    <span className="ms-2">Strava</span>
                </h5>
            </Card.Header>
            <Card.Body>
                {stravaStatus?.connected ? (
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <span className="text-success me-2">
                                <i className="bi bi-check-circle-fill"></i>
                            </span>
                            <a
                                href={`https://www.strava.com/athletes/${stravaStatus.athleteId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="strava-profile-link text-decoration-none"
                            >
                                Connected as <strong>{stravaStatus.athleteName}</strong>
                                <i className="bi bi-box-arrow-up-right text-muted small ms-1"></i>
                            </a>
                        </div>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={handleDisconnect}
                            disabled={isDisconnecting}
                        >
                            {isDisconnecting ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                'Disconnect'
                            )}
                        </Button>
                    </div>
                ) : (
                    <div className="text-center">
                        <p className="text-muted mb-3">
                            Connect your Strava account to automatically sync your activities.
                        </p>
                        <Button
                            className="btn-strava"
                            onClick={handleConnect}
                        >
                            <StravaIcon width={16} height={16} className="me-2" />
                            <span className="ms-1">Connect with Strava</span>
                        </Button>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

StravaConnectCard.propTypes = {
    isOwnProfile: PropTypes.bool.isRequired,
    stravaInfo: PropTypes.shape({
        connected: PropTypes.bool,
        athleteId: PropTypes.string,
        athleteName: PropTypes.string,
    }),
};

export default StravaConnectCard;
